import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Select, Space, Typography } from 'antd';
import React, { useCallback, useMemo } from 'react';

import { OFF } from '@app/constants';
import { useWindowSize } from '@app/hooks';
import type { NClientServices } from '@app/types/ClientServices';

import type { NClientServiceAdditions } from '@pages/Clients/EditClientService/components/ClientServiceParams/components/ClientServiceAdditions/ClientServiceAdditions.types';

const { Title } = Typography;

export const ClientServiceAdditions: NClientServiceAdditions.Main = ({
  additionsForm,
  allAdditions,
  unselectedAdditions,
  selectedAdditions,
  isLoader,
  setSelectedAdditions,
  setUnselectedAdditions,
}) => {
  const addAddition = useCallback(() => {
    setSelectedAdditions([
      ...selectedAdditions,
      {
        value: null,
        label: null,
      },
    ]);
  }, [setSelectedAdditions, selectedAdditions]);

  const deleteAddition = useCallback(
    (value: number) => {
      const newAddition = allAdditions.find(({ Id }) => Id === value);
      setSelectedAdditions(
        selectedAdditions.filter(({ value: _value }) => _value !== value)
      );
      if (newAddition) {
        setUnselectedAdditions([...unselectedAdditions, newAddition]);
      }
    },
    [
      allAdditions,
      selectedAdditions,
      setSelectedAdditions,
      setUnselectedAdditions,
      unselectedAdditions,
    ]
  );

  const adaptAdditionsToSelect = (
    unselectedAdditions: NClientServices.Service[]
  ) => {
    return unselectedAdditions?.map(({ Id, Name }) => ({
      value: Id,
      label: Name,
    }));
  };

  const memoizedUnselectedAdditions = useMemo(() => {
    return adaptAdditionsToSelect(unselectedAdditions);
  }, [unselectedAdditions]);

  const handleSelect = useCallback(
    (oldAdditionValue: number, newAdditionValue: number, index: number) => {
      const newAddition = allAdditions.find(
        ({ Id }) => Id === newAdditionValue
      );
      const adaptedNewAddition = {
        label: newAddition?.Name,
        value: newAddition?.Id,
      };
      setSelectedAdditions([
        ...selectedAdditions.filter(({ value }) => value !== oldAdditionValue),
        adaptedNewAddition,
      ]);
      setUnselectedAdditions(
        unselectedAdditions.filter(({ Id }) => Id !== newAdditionValue)
      );
      additionsForm.setFieldValue(`Addition${index}`, adaptedNewAddition);
    },
    [
      additionsForm,
      allAdditions,
      selectedAdditions,
      setSelectedAdditions,
      setUnselectedAdditions,
      unselectedAdditions,
    ]
  );

  const handleDropdownOpen = useCallback(
    (open: boolean) => {
      if (open) {
        setUnselectedAdditions(
          unselectedAdditions.filter(({ Id }) => {
            const isSelected = Boolean(
              selectedAdditions.find(({ value }) => value === Id)
            );
            if (isSelected) {
              return false;
            } else {
              return true;
            }
          })
        );
      }
    },
    [selectedAdditions, setUnselectedAdditions, unselectedAdditions]
  );

  const { innerWidth } = useWindowSize();
  const pageContentWidth =
    document.querySelector('.ant-tabs')?.getBoundingClientRect().width || 0;

  return (
    <Form
      form={additionsForm}
      initialValues={{ remember: true }}
      autoComplete={OFF}
    >
      <Title level={4}>Потенциальные допродажи</Title>

      <Space direction="vertical" className="full-width">
        {selectedAdditions?.map(({ value: oldAdditionValue }, index) => (
          <Space key={index}>
            <Form.Item style={{ marginBottom: 0 }} name={`Addition${index}`}>
              <Select
                className="employee-select"
                size="large"
                options={memoizedUnselectedAdditions}
                onSelect={(newAdditionValue) =>
                  handleSelect(oldAdditionValue, newAdditionValue, index)
                }
                onOpenChange={handleDropdownOpen}
                popupMatchSelectWidth={false}
                style={{
                  width:
                    innerWidth < 800
                      ? `${
                          Number(pageContentWidth) <= 400
                            ? pageContentWidth - 50
                            : 350
                        }px`
                      : '350px',
                }}
              />
            </Form.Item>
            <Button
              danger
              size="large"
              icon={<DeleteOutlined />}
              onClick={() => deleteAddition(oldAdditionValue)}
            />
          </Space>
        ))}

        <Button
          className="adding-button"
          size="large"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addAddition}
          loading={isLoader}
        >
          Добавить
        </Button>
      </Space>
    </Form>
  );
};
